<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CrmAPI from 'dashboard/api/crm';
import TeamsAPI from 'dashboard/api/teams';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CrmStageActions from './CrmStageActions.vue';

const props = defineProps({
  funnel: { type: Object, required: true },
  initialStageId: { type: String, default: '' },
  contained: { type: Boolean, default: false },
});
const emit = defineEmits(['saved', 'cancel']);
const { t } = useI18n();
const columns = ref(JSON.parse(JSON.stringify(props.funnel.columns)));
const accessTeamIds = ref([...(props.funnel.access_team_ids || [])]);
const teams = ref([]);
const selectedId = ref(props.initialStageId || columns.value[0]?.id);
const stage = computed(() =>
  columns.value.find(item => item.id === selectedId.value)
);
const busy = ref(false);
const error = ref('');
const members = ref([]);
const permissionBusy = ref(false);
const editingHours = ref(false);
const invalidSection = ref('');
const durationUnit = ref(60);
const intervalUnit = ref(60);
const unitOptions = computed(() => [
  { value: 60, label: t('CRM.settings.businessHours') },
  { value: 1, label: t('CRM.settings.businessMinutes') },
]);
const ownerOptions = computed(() => [
  { value: 'team', label: t('CRM.team') },
  { value: 'client', label: t('CRM.client') },
]);
const portfolioEventOptions = computed(() => [
  { value: 'none', label: t('CRM.settings.portfolioEvent.none') },
  {
    value: 'commercial_interaction',
    label: t('CRM.settings.portfolioEvent.commercialInteraction'),
  },
  {
    value: 'successful_sale',
    label: t('CRM.settings.portfolioEvent.successfulSale'),
  },
  { value: 'both', label: t('CRM.settings.portfolioEvent.both') },
]);
const stageOptions = computed(() =>
  columns.value.map((item, index) => ({
    value: item.id,
    label: item.name || `${t('CRM.newStage')} ${index + 1}`,
  }))
);
const days = [1, 2, 3, 4, 5, 6, 0];
columns.value.forEach(item => {
  item.requirements ||= [];
  item.classifications ||= [];
  item.hours ||= {};
  item.holidays ||= [];
  item.timezone ||= Intl.DateTimeFormat().resolvedOptions().timeZone;
  item.duration_minutes ||= 0;
  item.follow_up_limit ||= 0;
  item.follow_up_minutes ||= 60;
  item.portfolio_event ||= 'none';
});
watch(
  stage,
  value => {
    durationUnit.value = (value?.duration_minutes || 0) % 60 ? 1 : 60;
    intervalUnit.value = (value?.follow_up_minutes || 0) % 60 ? 1 : 60;
    editingHours.value = false;
    invalidSection.value = '';
  },
  { immediate: true }
);
const hoursSummary = computed(() => {
  const available = days.filter(day => stage.value?.hours[day]);
  if (!available.length) return t('CRM.settings.noHours');
  const sameHours = available.every(
    day =>
      stage.value.hours[day].join('–') ===
      stage.value.hours[available[0]].join('–')
  );
  if (sameHours && available.join(',') === '1,2,3,4,5') {
    return `${t('CRM.settings.weekdays')} · ${stage.value.hours[1].join('–')}`;
  }
  return available
    .map(day => `${t(`CRM.days.${day}`)} ${stage.value.hours[day].join('–')}`)
    .join(' · ');
});
const addStage = () => {
  const item = {
    id: crypto.randomUUID(),
    name: '',
    requirements: [],
    classifications: [],
    duration_minutes: 0,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    hours: {},
    holidays: [],
    follow_up_limit: 0,
    follow_up_minutes: 60,
    portfolio_event: 'none',
  };
  columns.value.push(item);
  selectedId.value = item.id;
};
const addRequirement = () =>
  stage.value.requirements.push({
    id: crypto.randomUUID(),
    name: '',
    owner: 'team',
  });
const addClassification = () =>
  stage.value.classifications.push({
    id: crypto.randomUUID(),
    name: '',
    options: ['', ''],
  });
const invalidClassification = classification =>
  !classification.name.trim() ||
  classification.options.length < 2 ||
  classification.options.some(option => !option.trim()) ||
  new Set(classification.options).size !== classification.options.length;
const minutesValue = (value, unit) => Math.round(Number(value) * unit);
const toggleTeam = (id, checked) => {
  accessTeamIds.value = checked
    ? [...accessTeamIds.value, id]
    : accessTeamIds.value.filter(item => item !== id);
};
onMounted(async () => {
  try {
    teams.value = (await TeamsAPI.get(true)).data;
  } catch {
    error.value = t('CRM.settings.teamAccessLoadError');
  }
});
const save = async () => {
  error.value = '';
  const invalid = columns.value.find(
    item =>
      !item.name.trim() ||
      item.requirements.some(req => !req.name.trim()) ||
      item.classifications.some(invalidClassification)
  );
  if (invalid) {
    selectedId.value = invalid.id;
    error.value = t('CRM.settings.requiredNames');
    await nextTick();
    invalidSection.value = invalid.classifications.some(invalidClassification)
      ? 'classifications'
      : 'requirements';
    return;
  }
  busy.value = true;
  try {
    await CrmAPI.settings(props.funnel.id, columns.value, accessTeamIds.value);
    emit('saved');
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    busy.value = false;
  }
};
const loadMembers = async () => {
  try {
    members.value = (await CrmAPI.permissions()).data;
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  }
};
const permission = async (member, checked) => {
  permissionBusy.value = true;
  try {
    members.value = (
      await CrmAPI.permissions({
        user_id: member.id,
        skip_confirmation: checked,
        funnel_id: props.funnel.id,
      })
    ).data;
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    permissionBusy.value = false;
  }
};
</script>

<template>
  <form
    class="flex min-w-0 flex-col text-sm text-n-slate-12"
    :class="contained ? 'h-full min-h-0' : 'gap-3'"
    novalidate
    @submit.prevent="save"
  >
    <div
      class="flex shrink-0 items-center gap-2 border-b border-n-weak"
      :class="contained ? 'p-4' : 'pb-3'"
    >
      <Button
        type="button"
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="sm"
        :aria-label="$t('CRM.cancel')"
        :disabled="busy"
        @click="emit('cancel')"
      />
      <h3 class="m-0 text-base font-semibold">
        {{ $t('CRM.settings.title') }}
      </h3>
    </div>
    <div :class="contained ? 'min-h-0 flex-1 overflow-y-auto p-4' : 'contents'">
      <div class="flex flex-col gap-3">
        <details class="rounded-lg border border-n-weak">
          <summary class="cursor-pointer p-3 font-medium">
            {{ $t('CRM.settings.teamAccessTitle') }}
          </summary>
          <div class="flex flex-col gap-2 px-3 pb-3">
            <p class="m-0 text-xs text-n-slate-11">
              {{ $t('CRM.settings.teamAccessHelp') }}
            </p>
            <p v-if="!accessTeamIds.length" class="m-0 text-xs text-n-slate-11">
              {{ $t('CRM.settings.teamAccessAll') }}
            </p>
            <label
              v-for="team in teams"
              :key="team.id"
              class="flex min-w-0 items-center gap-2 rounded-lg border border-n-weak p-2"
            >
              <input
                type="checkbox"
                :checked="accessTeamIds.includes(team.id)"
                @change="toggleTeam(team.id, $event.target.checked)"
              />
              <span class="min-w-0 break-words">{{ team.name }}</span>
            </label>
          </div>
        </details>
        <div class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('CRM.stage') }}</span>
          <div class="flex items-center gap-2">
            <ComboBox
              :allow-deselect="false"
              :model-value="selectedId"
              :options="stageOptions"
              class="min-w-0 flex-1"
              :aria-label="$t('CRM.stage')"
              @update:model-value="
                value => {
                  if (value) selectedId = value;
                }
              "
            />
            <Button
              type="button"
              variant="outline"
              color="slate"
              size="sm"
              icon="i-lucide-plus"
              class="!size-10 shrink-0"
              :aria-label="$t('CRM.addStage')"
              :title="$t('CRM.addStage')"
              @click="addStage"
            />
          </div>
        </div>
        <div v-if="stage" :key="stage.id" class="flex flex-col gap-3">
          <details open class="group rounded-lg border border-n-weak">
            <summary
              class="flex min-h-12 cursor-pointer list-none items-center gap-2 p-3 font-medium [&::-webkit-details-marker]:hidden"
            >
              <span
                class="i-lucide-clock size-4 shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
              <span class="flex-1">{{ $t('CRM.settings.general') }}</span>
              <span
                class="i-lucide-chevron-down size-4 text-n-slate-11 group-open:rotate-180"
                aria-hidden="true"
              />
            </summary>
            <div class="flex flex-col gap-3 px-3 pb-3">
              <label class="text-xs text-n-slate-11"
                >{{ $t('CRM.settings.stageName') }}
                <Input v-model="stage.name" size="sm" class="mt-1" />
              </label>
              <label class="text-xs text-n-slate-11">
                {{ $t('CRM.settings.portfolioEvent.label') }}
                <ComboBox
                  :allow-deselect="false"
                  :model-value="stage.portfolio_event"
                  :options="portfolioEventOptions"
                  class="mt-1"
                  :aria-label="$t('CRM.settings.portfolioEvent.label')"
                  @update:model-value="
                    value => (stage.portfolio_event = value || 'none')
                  "
                />
                <span class="mt-1 block leading-5">
                  {{ $t('CRM.settings.portfolioEvent.help') }}
                </span>
              </label>
              <div>
                <label
                  :for="`crm-duration-${stage.id}`"
                  class="text-xs text-n-slate-11"
                  >{{ $t('CRM.settings.defaultDeadline') }}</label
                >
                <div class="mt-1 flex items-center gap-2">
                  <Input
                    :id="`crm-duration-${stage.id}`"
                    :model-value="stage.duration_minutes / durationUnit"
                    type="number"
                    step="1"
                    size="sm"
                    class="w-20 shrink-0"
                    @update:model-value="
                      value =>
                        (stage.duration_minutes = Math.max(
                          0,
                          minutesValue(value, durationUnit)
                        ))
                    "
                  />
                  <ComboBox
                    :allow-deselect="false"
                    :model-value="durationUnit"
                    :options="unitOptions"
                    class="min-w-0 flex-1"
                    :aria-label="$t('CRM.settings.durationUnit')"
                    @update:model-value="
                      value => {
                        if (value) durationUnit = value;
                      }
                    "
                  />
                </div>
                <p
                  v-if="!stage.duration_minutes"
                  class="mb-0 mt-1 text-xs text-n-slate-11"
                >
                  {{ $t('CRM.noDeadline') }}
                </p>
              </div>
              <div
                class="flex flex-wrap items-center justify-between gap-2 border-t border-n-weak pt-3"
              >
                <div class="min-w-0 flex-1 text-xs text-n-slate-11">
                  <p class="m-0 flex items-center gap-1.5">
                    <span
                      class="i-lucide-clock size-3 shrink-0"
                      aria-hidden="true"
                    /><span>{{ hoursSummary }}</span>
                  </p>
                </div>
                <Button
                  v-tooltip="$t('CRM.settings.editHours')"
                  type="button"
                  variant="outline"
                  color="slate"
                  size="xs"
                  icon="i-lucide-pencil"
                  class="bg-n-solid-3"
                  :aria-label="$t('CRM.settings.editHours')"
                  :aria-expanded="editingHours"
                  @click="editingHours = !editingHours"
                />
              </div>
              <div
                v-if="editingHours"
                class="flex flex-col gap-3 border-t border-n-weak pt-3"
              >
                <label class="text-xs text-n-slate-11"
                  >{{ $t('CRM.timezone')
                  }}<input
                    v-model="stage.timezone"
                    class="mt-1 h-9 w-full rounded-lg border border-n-slate-7 bg-n-field px-2 text-sm text-n-slate-12"
                /></label>
                <fieldset class="flex min-w-0 flex-col gap-2">
                  <legend class="mb-2 text-xs text-n-slate-11">
                    {{ $t('CRM.calendar') }}
                  </legend>
                  <div
                    v-for="day in days"
                    :key="day"
                    class="flex flex-wrap items-center gap-2"
                  >
                    <label class="flex min-w-28 items-center gap-2 text-xs"
                      ><input
                        type="checkbox"
                        :checked="!!stage.hours[day]"
                        @change="
                          $event.target.checked
                            ? (stage.hours[day] = ['09:00', '17:00'])
                            : delete stage.hours[day]
                        "
                      />{{ $t(`CRM.days.${day}`) }}</label
                    >
                    <template v-if="stage.hours[day]">
                      <input
                        v-model="stage.hours[day][0]"
                        type="time"
                        :aria-label="`${$t('CRM.days.' + day)} · ${$t('CRM.opens')}`"
                        class="min-w-0 w-24 rounded border border-n-slate-7 bg-n-field p-1 text-xs"
                      />
                      <input
                        v-model="stage.hours[day][1]"
                        type="time"
                        :aria-label="`${$t('CRM.days.' + day)} · ${$t('CRM.closes')}`"
                        class="min-w-0 w-24 rounded border border-n-slate-7 bg-n-field p-1 text-xs"
                      />
                    </template>
                  </div>
                </fieldset>
                <label class="text-xs text-n-slate-11"
                  >{{ $t('CRM.holidays')
                  }}<textarea
                    :value="stage.holidays.join('\n')"
                    class="mt-1 w-full rounded-lg border border-n-slate-7 bg-n-field p-2 text-sm text-n-slate-12"
                    @input="
                      stage.holidays = $event.target.value
                        .split('\n')
                        .map(s => s.trim())
                        .filter(Boolean)
                    "
                  />
                </label>
              </div>
            </div>
          </details>
          <details
            :open="invalidSection === 'classifications'"
            class="group rounded-lg border border-n-weak"
          >
            <summary
              class="flex min-h-12 cursor-pointer list-none items-center gap-2 p-3 [&::-webkit-details-marker]:hidden"
            >
              <span
                class="i-lucide-tags size-4 shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
              <span class="flex-1 font-medium">{{
                $t('CRM.classifications.title')
              }}</span>
              <span class="text-xs text-n-slate-11">{{
                stage.classifications.length
              }}</span>
              <span
                class="i-lucide-chevron-down size-4 text-n-slate-11 group-open:rotate-180"
                aria-hidden="true"
              />
            </summary>
            <div class="flex flex-col gap-3 px-3 pb-3">
              <p class="m-0 text-xs text-n-slate-11">
                {{ $t('CRM.classifications.settingsHelp') }}
              </p>
              <div
                v-for="(classification, index) in stage.classifications"
                :key="classification.id"
                class="flex flex-col gap-2 border-t border-n-weak pt-3"
              >
                <div class="flex items-center gap-2">
                  <Input
                    v-model="classification.name"
                    size="sm"
                    class="min-w-0 flex-1"
                    :aria-label="$t('CRM.classifications.name')"
                    :placeholder="$t('CRM.classifications.name')"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    color="ruby"
                    size="sm"
                    icon="i-lucide-trash-2"
                    :aria-label="$t('CRM.remove')"
                    @click="stage.classifications.splice(index, 1)"
                  />
                </div>
                <div
                  v-for="(option, optionIndex) in classification.options"
                  :key="optionIndex"
                  class="flex items-center gap-2"
                >
                  <Input
                    v-model="classification.options[optionIndex]"
                    size="sm"
                    class="min-w-0 flex-1"
                    :aria-label="`${$t('CRM.classifications.option')} ${optionIndex + 1}`"
                    :placeholder="`${$t('CRM.classifications.option')} ${optionIndex + 1}`"
                  />
                  <Button
                    v-if="classification.options.length > 2"
                    type="button"
                    variant="ghost"
                    color="slate"
                    size="sm"
                    icon="i-lucide-x"
                    :aria-label="$t('CRM.remove')"
                    @click="classification.options.splice(optionIndex, 1)"
                  />
                </div>
                <Button
                  v-if="classification.options.length < 10"
                  type="button"
                  variant="outline"
                  color="slate"
                  size="sm"
                  icon="i-lucide-plus"
                  :label="$t('CRM.classifications.addOption')"
                  @click="classification.options.push('')"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-plus"
                :label="$t('CRM.classifications.add')"
                :disabled="stage.classifications.length >= 10"
                @click="addClassification"
              />
            </div>
          </details>
          <details
            :open="invalidSection === 'requirements'"
            class="group rounded-lg border border-n-weak"
          >
            <summary
              class="flex min-h-12 cursor-pointer list-none items-center gap-2 p-3 [&::-webkit-details-marker]:hidden"
            >
              <span
                class="i-lucide-file-text mt-0.5 size-4 self-start shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
              <span class="flex-1"
                ><span class="block font-medium">{{
                  $t('CRM.progressItems.title')
                }}</span
                ><span class="text-xs text-n-slate-11">{{
                  $t('CRM.settings.requirementCount', {
                    count: stage.requirements.length,
                  })
                }}</span></span
              >
              <span
                class="i-lucide-chevron-down size-4 text-n-slate-11 group-open:rotate-180"
                aria-hidden="true"
              />
            </summary>
            <div class="flex flex-col gap-3 px-3 pb-3">
              <div
                v-for="(requirement, index) in stage.requirements"
                :key="requirement.id"
                class="flex flex-col gap-2 rounded-lg border border-n-weak p-2"
              >
                <Input
                  v-model="requirement.name"
                  size="sm"
                  :aria-label="$t('CRM.requirementName')"
                />
                <div class="flex items-center gap-2">
                  <ComboBox
                    :allow-deselect="false"
                    :model-value="requirement.owner"
                    :options="ownerOptions"
                    class="min-w-0 flex-1"
                    :aria-label="$t('CRM.responsibility')"
                    @update:model-value="
                      value => {
                        if (value) requirement.owner = value;
                      }
                    "
                  />
                  <Button
                    type="button"
                    variant="outline"
                    color="ruby"
                    size="sm"
                    icon="i-lucide-trash-2"
                    :aria-label="$t('CRM.remove')"
                    :title="$t('CRM.remove')"
                    @click="stage.requirements.splice(index, 1)"
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                color="slate"
                size="sm"
                icon="i-lucide-plus"
                class="bg-n-solid-3"
                :label="$t('CRM.addRequirement')"
                @click="addRequirement"
              />
            </div>
          </details>
          <details class="group rounded-lg border border-n-weak">
            <summary
              class="flex min-h-12 cursor-pointer list-none items-center gap-2 p-3 [&::-webkit-details-marker]:hidden"
            >
              <span
                class="i-lucide-refresh-cw mt-0.5 size-4 self-start shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
              <span class="flex-1"
                ><span class="block font-medium">{{ $t('CRM.followUp') }}</span
                ><span class="text-xs text-n-slate-11">{{
                  $t('CRM.attemptCount', { count: stage.follow_up_limit })
                }}</span></span
              >
              <span
                class="i-lucide-chevron-down size-4 text-n-slate-11 group-open:rotate-180"
                aria-hidden="true"
              />
            </summary>
            <div class="flex flex-col gap-3 px-3 pb-3">
              <label class="text-xs text-n-slate-11"
                >{{ $t('CRM.followUpLimit')
                }}<Input
                  :model-value="stage.follow_up_limit"
                  type="number"
                  step="1"
                  size="sm"
                  class="mt-1"
                  @update:model-value="
                    value =>
                      (stage.follow_up_limit = Math.max(
                        0,
                        Math.floor(Number(value))
                      ))
                  "
              /></label>
              <div>
                <label
                  :for="`crm-interval-${stage.id}`"
                  class="text-xs text-n-slate-11"
                  >{{ $t('CRM.settings.interval') }}</label
                >
                <div class="mt-1 flex items-center gap-2">
                  <Input
                    :id="`crm-interval-${stage.id}`"
                    :model-value="stage.follow_up_minutes / intervalUnit"
                    type="number"
                    step="1"
                    size="sm"
                    class="w-20 shrink-0"
                    @update:model-value="
                      value =>
                        (stage.follow_up_minutes = Math.max(
                          0,
                          minutesValue(value, intervalUnit)
                        ))
                    "
                  />
                  <ComboBox
                    :allow-deselect="false"
                    :model-value="intervalUnit"
                    :options="unitOptions"
                    class="min-w-0 flex-1"
                    :aria-label="$t('CRM.settings.durationUnit')"
                    @update:model-value="
                      value => {
                        if (value) intervalUnit = value;
                      }
                    "
                  />
                </div>
              </div>
            </div>
          </details>
        </div>
        <CrmStageActions
          v-if="stage"
          :key="stage.id"
          v-model:macro-ids="stage.macro_ids"
          v-model:automation-rule-ids="stage.automation_rule_ids"
        />
        <details
          class="group rounded-lg border border-n-weak"
          @toggle="$event.target.open && loadMembers()"
        >
          <summary
            class="flex min-h-12 cursor-pointer list-none items-center gap-2 p-3 [&::-webkit-details-marker]:hidden"
          >
            <span
              class="i-lucide-user-round mt-0.5 size-4 self-start shrink-0 text-n-slate-11"
              aria-hidden="true"
            />
            <span class="flex-1"
              ><span class="block font-medium">{{
                $t('CRM.settings.permissions')
              }}</span
              ><span class="text-xs text-n-slate-11">{{
                $t('CRM.settings.perAgent')
              }}</span></span
            >
            <span
              class="i-lucide-chevron-down size-4 text-n-slate-11 group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>
          <div class="flex flex-col gap-3 px-3 pb-3">
            <p class="m-0 text-sm text-n-slate-12">
              {{ $t('CRM.skipConfirmation') }}
            </p>
            <p class="m-0 text-xs text-n-slate-11">
              {{ $t('CRM.settings.permissionHelp') }}
            </p>
            <label
              v-for="member in members"
              :key="member.id"
              class="flex items-center gap-2 rounded-lg border border-n-weak p-2 text-sm"
              ><input
                type="checkbox"
                :checked="member.skip_confirmation"
                :disabled="permissionBusy"
                @change="permission(member, $event.target.checked)"
              /><span>{{ member.name }}</span></label
            >
          </div>
        </details>
        <p
          v-if="error"
          role="alert"
          class="m-0 rounded-lg bg-n-ruby-3 p-3 text-xs text-n-ruby-11"
        >
          {{ error }}
        </p>
      </div>
    </div>
    <div
      class="z-10 flex shrink-0 flex-col gap-3 border-t border-n-weak bg-n-solid-2"
      :class="contained ? 'p-4' : 'sticky bottom-0 py-3'"
    >
      <p
        class="m-0 flex items-center gap-2 rounded-lg border border-n-weak bg-n-solid-3 p-2 text-xs text-n-slate-11"
      >
        <span class="i-lucide-info size-4 shrink-0" aria-hidden="true" />{{
          $t('CRM.settings.newCycles')
        }}
      </p>
      <div class="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          color="slate"
          size="sm"
          class="bg-n-solid-3"
          :label="$t('CRM.cancel')"
          :disabled="busy"
          @click="emit('cancel')"
        />
        <Button
          type="submit"
          color="blue"
          size="sm"
          icon="i-lucide-save"
          :label="$t('CRM.settings.saveChanges')"
          :disabled="busy || permissionBusy"
        />
      </div>
    </div>
  </form>
</template>
